<template>
  <div>
    <h2 class="text-lg">Date created</h2>
    <div class="mt-4">
      <h3 class="mb-2 block text-base font-medium text-gray-700 select-none">Choose a Preset</h3>
      <ul class="flex flex-wrap -m-1">
        <li v-for="presetOption in presetOptions" :key="presetOption.value" class="p-1">
          <BaseButton primary outline small @click="setFromPreset(presetOption.value)">
            {{ presetOption.label }}
          </BaseButton>
        </li>
      </ul>
    </div>
    <div class="mt-4">
      <h3 class="mb-2 block text-base font-medium text-gray-700 select-none">And/Or Customize</h3>
      <div
        class="-mx-6 px-6 space-y-2 py-4"
        :class="[selectedFilter === 'date_created_is' ? 'bg-blue-50' : 'bg-white']"
      >
        <div class="flex items-center">
          <input
            type="radio"
            v-model="selectedFilter"
            name="date_created"
            id="date_created_is"
            value="date_created_is"
          />
          <label
            for="date_created_is"
            class="ml-3 block text-sm font-medium text-gray-700 select-none"
            >Date created is</label
          >
        </div>
        <BaseFormInput
          v-show="selectedFilter === 'date_created_is'"
          type="date"
          v-model="valueByFilter.date_created_is"
          class="w-auto"
        />
      </div>

      <div
        class="-mx-6 px-6 space-y-2 py-4"
        :class="[selectedFilter === 'date_created_is_not' ? 'bg-blue-50' : 'bg-white']"
      >
        <div class="flex items-center">
          <input
            type="radio"
            v-model="selectedFilter"
            name="date_created"
            id="date_created_is_not"
            value="date_created_is_not"
          />
          <label
            for="date_created_is_not"
            class="ml-3 block text-sm font-medium text-gray-700 select-none"
            >Date created is not</label
          >
        </div>
        <BaseFormInput
          v-show="selectedFilter === 'date_created_is_not'"
          type="date"
          v-model="valueByFilter.date_created_is_not"
          class="w-auto"
        />
      </div>

      <div
        class="-mx-6 px-6 space-y-2 py-4"
        :class="[selectedFilter === 'date_created_after' ? 'bg-blue-50' : 'bg-white']"
      >
        <div class="flex items-center">
          <input
            type="radio"
            v-model="selectedFilter"
            name="date_created"
            id="date_created_after"
            value="date_created_after"
          />
          <label
            for="date_created_after"
            class="ml-3 block text-sm font-medium text-gray-700 select-none"
            >Date created after</label
          >
        </div>
        <BaseFormInput
          v-show="selectedFilter === 'date_created_after'"
          type="date"
          v-model="valueByFilter.date_created_after"
          class="w-auto"
        />
      </div>

      <div
        class="-mx-6 px-6 space-y-2 py-4"
        :class="[selectedFilter === 'date_created_before' ? 'bg-blue-50' : 'bg-white']"
      >
        <div class="flex items-center">
          <input
            type="radio"
            v-model="selectedFilter"
            name="date_created"
            id="date_created_before"
            value="date_created_before"
          />
          <label
            for="date_created_before"
            class="ml-3 block text-sm font-medium text-gray-700 select-none"
            >Date created before</label
          >
        </div>
        <BaseFormInput
          v-show="selectedFilter === 'date_created_before'"
          type="date"
          v-model="valueByFilter.date_created_before"
          class="w-auto"
        />
      </div>

      <div
        class="-mx-6 px-6 space-y-2 py-4"
        :class="[selectedFilter === 'date_created_between' ? 'bg-blue-50' : 'bg-white']"
      >
        <div class="flex items-center">
          <input
            type="radio"
            v-model="selectedFilter"
            name="date_created"
            id="date_created_between"
            value="date_created_between"
          />
          <label
            for="date_created_between"
            class="ml-3 block text-sm font-medium text-gray-700 select-none"
            >Date created between</label
          >
        </div>
        <div v-show="selectedFilter === 'date_created_between'" class="flex items-center space-x-3">
          <BaseFormInput
            type="date"
            v-model="valueByFilter.date_created_between[0]"
            class="w-auto"
          />
          <span>-</span>
          <BaseFormInput
            type="date"
            v-model="valueByFilter.date_created_between[1]"
            class="w-auto"
          />
        </div>
      </div>
    </div>
    <div class="mt-5 flex space-x-3">
      <div class="flex-none">
        <button
          type="button"
          class="relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md bg-white focus:outline-none border-gray-300 text-gray-700 hover:bg-gray-50 focus:border-blue-600"
          @click="$emit('cancel')"
        >
          Cancel
        </button>
      </div>
      <div class="flex-1">
        <button
          type="button"
          class="w-full relative text-center items-center px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white border border-transparent focus:outline-none focus:ring ring-blue-500 ring-opacity-50 focus:border-blue-700 disabled:bg-gray-200 disabled:text-gray-700"
          :disabled="!isValid"
          @click="applyFilter"
        >
          Apply Filter
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import * as datePresetTypes from '@/utils/date-preset-types';
import {
  FILTER_DATE_CREATED_AFTER,
  FILTER_DATE_CREATED_BEFORE,
  FILTER_DATE_CREATED_BETWEEN,
  FILTER_DATE_CREATED_IS,
  FILTER_DATE_CREATED_IS_NOT,
} from './filter-options';

export const filterByPreset = {
  [datePresetTypes.TODAY]: {
    name: FILTER_DATE_CREATED_IS,
    getValue: () => moment().format('YYYY-MM-DD'),
  },
  [datePresetTypes.YESTERDAY]: {
    name: FILTER_DATE_CREATED_IS,
    getValue: () => moment().subtract(1, 'day').format('YYYY-MM-DD'),
  },
  [datePresetTypes.THIS_WEEK]: {
    name: FILTER_DATE_CREATED_AFTER,
    getValue: () => moment().startOf('isoWeek').subtract(1, 'days').format('YYYY-MM-DD'),
  },
  [datePresetTypes.THIS_MONTH]: {
    name: FILTER_DATE_CREATED_AFTER,
    getValue: () => moment().startOf('month').subtract(1, 'days').format('YYYY-MM-DD'),
  },
  [datePresetTypes.THIS_YEAR]: {
    name: FILTER_DATE_CREATED_AFTER,
    getValue: () => moment().startOf('year').subtract(1, 'days').format('YYYY-MM-DD'),
  },
  [datePresetTypes.LAST_WEEK]: {
    name: FILTER_DATE_CREATED_BETWEEN,
    getValue: () => {
      const until = moment().startOf('isoWeek').subtract(1, 'days').format('YYYY-MM-DD');
      const since = moment(until).startOf('isoWeek').format('YYYY-MM-DD');

      return [since, until];
    },
  },
  [datePresetTypes.LAST_MONTH]: {
    name: FILTER_DATE_CREATED_BETWEEN,
    getValue: () => {
      const until = moment().startOf('month').subtract(1, 'days').format('YYYY-MM-DD');
      const since = moment(until).startOf('month').format('YYYY-MM-DD');

      return [since, until];
    },
  },
  [datePresetTypes.LAST_YEAR]: {
    name: FILTER_DATE_CREATED_BETWEEN,
    getValue: () => {
      const until = moment().startOf('year').subtract(1, 'days').format('YYYY-MM-DD');
      const since = moment(until).startOf('year').format('YYYY-MM-DD');

      return [since, until];
    },
  },
};

const toArrayWithoutEmpty = (value) => (Array.isArray(value) ? value : [value]).filter((d) => d);

const isValidDate = (date) => {
  if (!date) {
    return false;
  }

  return moment(date, 'YYYY-MM-DD').isValid();
};

const isValidDateRange = (dateA, dateB) => moment(dateA).isBefore(dateB);

export default {
  data() {
    return {
      selectedFilter: null,
      valueByFilter: {
        [FILTER_DATE_CREATED_IS]: '',
        [FILTER_DATE_CREATED_IS_NOT]: '',
        [FILTER_DATE_CREATED_AFTER]: '',
        [FILTER_DATE_CREATED_BEFORE]: '',
        [FILTER_DATE_CREATED_BETWEEN]: ['', ''],
      },
      presetOptions: [
        {
          value: datePresetTypes.TODAY,
          label: 'Today',
        },
        {
          value: datePresetTypes.YESTERDAY,
          label: 'Yesterday',
        },
        {
          value: datePresetTypes.THIS_WEEK,
          label: 'This week',
        },
        {
          value: datePresetTypes.THIS_MONTH,
          label: 'This month',
        },
        {
          value: datePresetTypes.THIS_YEAR,
          label: 'This year',
        },
        {
          value: datePresetTypes.LAST_WEEK,
          label: 'Last week',
        },
        {
          value: datePresetTypes.LAST_MONTH,
          label: 'Last month',
        },
        {
          value: datePresetTypes.LAST_YEAR,
          label: 'Last year',
        },
      ],
    };
  },
  computed: {
    isValid() {
      const dates = toArrayWithoutEmpty(this.valueByFilter[this.selectedFilter]);

      if (dates.length === 0) {
        return false;
      }

      if (dates.some((date) => !isValidDate(date))) {
        return false;
      }

      if (dates.length === 2) {
        return isValidDateRange(dates[0], dates[1]);
      }

      return true;
    },
  },
  methods: {
    setFromPreset(preset) {
      const presetFilter = filterByPreset[preset];

      if (typeof presetFilter === 'undefined') {
        return;
      }

      const value = presetFilter.getValue();

      this.selectedFilter = presetFilter.name;
      this.valueByFilter[presetFilter.name] = value;
    },
    applyFilter() {
      if (!this.isValid) {
        return;
      }

      this.$emit('apply-filter', {
        key: this.selectedFilter,
        value: this.valueByFilter[this.selectedFilter],
      });
    },
  },
};
</script>
